import React, { useState } from 'react';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { Label, Button, InputText } from 'design-system/Form';
import apiFetch, { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';
import { Select, Option } from 'react-scale/components/design-system';
import { IUserJson } from '@scale/shared/types/user';

const description = `
### Description:
- This tool allows you to mark Bee TTS batches as Ready for Customer.
- Select the project and batch, and then click the button to mark the batch as ready or not ready for customer.

##### Author: Carlos Gonzalez
---
`;

const APPLE_CUSTOMER_ID = '619453fb628b9200344887e2';

const MarkBatchReady: React.FC<{ user: IUserJson }> = ({ user }) => {
  const [batches, setBatches] = useState<string[] | null>();
  const [ready, setReady] = useState<boolean>();
  const [message, setMessage] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);

  const renderOutput = () => {
    return (
      <div className="response-markdown">
        <SanitizedMarkdown text={message || ''} />
        <style jsx global>{`
          .response-markdown tr {
            border: 1px solid rgba(0, 0, 0, 0.3);
          }

          .response-markdown tr:nth-child(even) {
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgb(0, 0, 0, 0.3);
            padding: 6px 10px;
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgba(255, 255, 255, 0.3);
            padding: 6px 10px;
            background-color: #222;
            color: white;
          }

          .response-markdown td {
            border: 1px solid rgba(0, 0, 0, 0.3);
            padding: 10px;
          }
        `}</style>
      </div>
    );
  };

  const handleMarkAsReady = async () => {
    setLoading(true);
    // eslint-disable-next-line no-console
    console.log(`Marking batches ${batches} as ready for customer`);

    try {
      const res = await apiFetch(null, 'corp-api/bee-tts-utils/metadata', {
        method: 'PUT',
        body: {
          batchNames: batches,
          customerId: APPLE_CUSTOMER_ID,
          submitterEmail: user.email,
          metadata: {
            ready,
          },
        },
      });
      setBatches([]);
      setMessage(res.message);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
    setLoading(false);
  };

  const onChangeBatches = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newBatches = event.target.value.split(',').map(batch => batch.trim());
    setBatches(newBatches);
  };

  const selectBatch = () => {
    return (
      <div className="inputComponent">
        <InputText
          placeholder="Batches Names (comma separated)"
          value={batches?.join(',')}
          onChange={onChangeBatches}
        />
      </div>
    );
  };

  const onChangeReady = (event: React.ChangeEvent<{ value: boolean }>) => {
    setReady(!!event.target?.value);
  };

  const buttonText = () => {
    if (!batches) return 'Select batches';
    if (ready === undefined) return 'Select desired ready state';
    if (loading) return 'Loading...';
    return 'Change batch Ready state';
  };

  return (
    <div className="root">
      <section>
        <SanitizedMarkdown text={description} />
      </section>

      <section className="two-columns">
        <section>
          <Label>Batches:</Label>
          {selectBatch()}
        </section>
        <section>
          <Label>Ready:</Label>
          <Select value={ready} onChange={onChangeReady}>
            <Option key="true" value={true}>
              true
            </Option>
            <Option key="false" value={false}>
              false
            </Option>
          </Select>
        </section>
      </section>
      <br />
      <section>
        <Button
          primary
          disabled={!batches || !batches.length || ready === undefined || loading}
          requireConfirmation
          onClick={handleMarkAsReady}
        >
          {buttonText()}
        </Button>
        {message && renderOutput()}
      </section>
      <style jsx>{`
        .root .two-columns {
          display: flex;
        }
        .root .two-columns section {
          flex-basis: 50%;
        }
        .root .two-columns section.disabled {
          color: rgba(0, 0, 0, 0.3);
        }
        .root .inputComponent {
          margin-top: 12px;
        }
      `}</style>
    </div>
  );
};

export default MarkBatchReady;
